import { memo, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Diamond,
  Link2Off,
  PenLine,
  X,
  type LucideIcon
} from 'lucide-react';
import { Badge, Button, Card, StepNumberBadge } from '@bliks-io/ui';
import type {
  DraftIssue,
  DraftIssueGroup,
  DraftIssueNodeKind,
  DraftIssueQuickFix
} from '../publish/draftIssues';

export const DRAFT_ISSUES_SIDEBAR_ID = 'draft-issues-sidebar';

interface IssueHandlers {
  onGoToIssue: (issue: DraftIssue) => void;
  onQuickFix: (fix: DraftIssueQuickFix) => void;
  /** Set on every row and quick-fix button. */
  buttonRef?: Ref<HTMLButtonElement>;
}

interface Props extends IssueHandlers {
  groups: readonly DraftIssueGroup[];
  onClose: () => void;
}

const GROUP_ICON: Partial<Record<DraftIssueNodeKind, LucideIcon>> = {
  event: Circle,
  decision: Diamond
};

function NodeGlyph({ group }: { group: DraftIssueGroup }) {
  if (group.stepNumber !== undefined) return <StepNumberBadge number={group.stepNumber} />;
  const Icon = GROUP_ICON[group.nodeKind] ?? AlertCircle;
  return <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />;
}

/** One issue: a go-to button (plain text when process-level) and its quick fix. */
function DraftIssueRow({
  issue,
  onGoToIssue,
  onQuickFix,
  buttonRef
}: IssueHandlers & { issue: DraftIssue }) {
  const { t } = useTranslation();
  const { quickFix } = issue;
  const KindIcon = issue.category === 'missing' ? PenLine : Link2Off;
  const content = (
    <>
      <KindIcon aria-hidden className="mt-0.5 size-3.5 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-foreground">{issue.title}</span>
        {issue.hint && <span className="block text-muted-foreground">{issue.hint}</span>}
      </span>
    </>
  );
  const rowClassName = 'flex w-full items-start gap-2 text-left text-xs';

  return (
    <li className="px-3 py-2">
      {issue.nodeId === null ? (
        <div className={rowClassName}>{content}</div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => onGoToIssue(issue)}
          className={`${rowClassName} rounded-sm hover:underline`}
        >
          {content}
        </button>
      )}
      {quickFix && (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => onQuickFix(quickFix)}
          className="mt-1.5 ml-5.5 rounded-sm border border-border px-1.5 py-0.5 text-xs font-medium hover:bg-muted"
        >
          {t('processFlow.contextMenu.markAsEnd')}
        </button>
      )}
    </li>
  );
}

export function DraftIssueList({
  issues,
  ...handlers
}: IssueHandlers & { issues: readonly DraftIssue[] }) {
  return (
    <ul className="divide-y divide-border">
      {issues.map((issue) => (
        <DraftIssueRow key={issue.key} issue={issue} {...handlers} />
      ))}
    </ul>
  );
}

/** Every publish issue of the draft, one group per node in flow order. */
export const DraftIssuesSidebar = memo(function DraftIssuesSidebar({
  groups,
  onClose,
  ...handlers
}: Props) {
  const { t } = useTranslation();
  const issueCount = groups.reduce((sum, group) => sum + group.issues.length, 0);

  return (
    <aside
      id={DRAFT_ISSUES_SIDEBAR_ID}
      aria-label={t('processDraft.issues.title')}
      className="flex w-80 shrink-0 flex-col border-r border-border bg-background"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            {issueCount === 0 ? (
              <CheckCircle2 aria-hidden className="size-4 text-success" />
            ) : (
              <AlertCircle aria-hidden className="size-4 text-destructive" />
            )}
            {t('processDraft.issues.title')}
          </h2>
          {issueCount > 0 && <Badge variant="destructive">{issueCount}</Badge>}
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('common.close')}>
          <X aria-hidden />
        </Button>
      </div>

      {issueCount === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <CheckCircle2 aria-hidden className="size-10 text-success" />
          <p className="text-sm font-medium">{t('processDraft.issues.none')}</p>
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto p-3">
          {groups.map((group) => (
            <Card key={group.nodeId ?? 'process'} role="group" aria-label={group.nodeLabel}>
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <NodeGlyph group={group} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {group.nodeLabel}
                </span>
              </div>
              <DraftIssueList issues={group.issues} {...handlers} />
            </Card>
          ))}
        </div>
      )}
    </aside>
  );
});
